import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Lock, FileText, Loader2 } from 'lucide-react';
import api from '../utils/api';

/**
 * Template Selector Modal
 * Displays available templates for the user to choose before generating output
 * 
 * Props:
 * - open: boolean - whether modal is open
 * - onOpenChange: function - callback when modal state changes
 * - onSelect: function(templateId) - callback when template is selected
 * - outputType: string - 'pdf' | 'docx' | 'email' | 'link' - type of output being generated
 * - title: string - optional custom title
 */
const TemplateSelector = ({ open, onOpenChange, onSelect, outputType = 'pdf', title, themeStyle = {}, accentTextColor = '#ffffff' }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [allowChoice, setAllowChoice] = useState(true);
  const [theme, setTheme] = useState('regular');
  const needsTheme = ['pdf', 'docx', 'email'].includes(outputType);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/templates/tenant/available');
      const templatesData = response.data.templates || [];
      const tenantDefaultId = response.data.default_template_id;
      const choiceAllowed = response.data.allow_template_choice ?? true;
      
      setTemplates(templatesData);
      setAllowChoice(choiceAllowed);
      
      // Auto-select tenant's default, then system default, then first accessible
      const tenantDefault = templatesData.find(t => t.template_id === tenantDefaultId && t.is_available);
      const systemDefault = templatesData.find(t => t.is_default && t.is_available);
      const firstAccessible = templatesData.find(t => t.is_available);
      setSelectedTemplate(tenantDefault?.template_id || systemDefault?.template_id || firstAccessible?.template_id || null);
      
      // If choice is not allowed and we have a default, auto-select and close
      if (!choiceAllowed && outputType === 'link' && (tenantDefault || systemDefault || firstAccessible)) {
        const defaultId = tenantDefault?.template_id || systemDefault?.template_id || firstAccessible?.template_id;
        if (defaultId) {
          onSelect(defaultId);
          onOpenChange(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Fallback to old endpoint
      try {
        const fallbackResponse = await api.get('/templates/all-with-access');
        setTemplates(fallbackResponse.data.map(t => ({
          ...t,
          is_available: t.has_access,
          is_tenant_default: false
        })));
        const defaultTemplate = fallbackResponse.data.find(t => t.is_default && t.has_access);
        const firstAccessible = fallbackResponse.data.find(t => t.has_access);
        setSelectedTemplate(defaultTemplate?.template_id || firstAccessible?.template_id || null);
      } catch (e) {
        setTemplates([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate, theme);
      onOpenChange(false);
    }
  };

  const outputLabels = {
    pdf: 'Download PDF',
    docx: 'Download Word',
    email: 'Email HTML',
    link: 'Share Link'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl quotation-theme" style={themeStyle}>
        <DialogHeader>
          <DialogTitle>{title || 'Select Template'}</DialogTitle>
          <DialogDescription>
            Choose a template design for your {outputLabels[outputType] || 'output'}
          </DialogDescription>
        </DialogHeader>
        {needsTheme && (
          <div className="space-y-1" data-testid="output-theme-picker">
            <p className="text-sm font-medium">Theme Style</p>
            <div className="grid grid-cols-3 gap-2">
              {[['short', 'Short', 'Essentials only'], ['regular', 'Regular', 'Adds day-wise plan, inclusions, payment policy'],
                ['detailed', 'Detailed', 'Everything incl. Terms & Cancellation']].map(([k, l, d]) => (
                <button key={k} type="button" onClick={() => setTheme(k)} data-testid={`output-theme-${k}`}
                  className={`text-left rounded-md border p-2 transition-colors ${theme === k ? 'border-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_8%,white)]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <span className="block text-sm font-semibold">{l}</span>
                  <span className="block text-[11px] text-gray-500">{d}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No templates available</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto py-2">
              {templates.map((template) => {
                const hasAccess = template.is_available ?? template.has_access;
                return (
                <div
                  key={template.template_id}
                  onClick={() => hasAccess && setSelectedTemplate(template.template_id)}
                  className={`
                    relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all
                    ${selectedTemplate === template.template_id 
                      ? 'border-[var(--cyan)] ring-2 ring-[color-mix(in_srgb,var(--cyan)_30%,white)]' 
                      : hasAccess 
                        ? 'border-gray-200 hover:border-gray-300' 
                        : 'border-gray-200 opacity-60 cursor-not-allowed'
                    }
                  `}
                  data-testid={`template-option-${template.template_slug}`}
                >
                  {/* Thumbnail */}
                  <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 relative">
                    {template.thumbnail_url ? (
                      <img 
                        src={template.thumbnail_url} 
                        alt={template.template_name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="w-10 h-10 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Selection indicator */}
                    {selectedTemplate === template.template_id && (
                      <div className="absolute top-2 right-2 bg-[var(--cyan)] text-white rounded-full p-1" style={{ color: accentTextColor }}>
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}

                    {/* Locked indicator */}
                    {!hasAccess && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="bg-white/90 px-3 py-1.5 rounded-full flex items-center gap-1 text-sm">
                          <Lock className="w-3 h-3" />
                          Upgrade
                        </div>
                      </div>
                    )}

                    {/* Tenant Default badge */}
                    {template.is_tenant_default && (
                      <Badge className="absolute top-2 left-2 bg-[var(--navy)] text-white text-xs">
                        Your Default
                      </Badge>
                    )}
                    
                    {/* System Default badge */}
                    {template.is_default && !template.is_tenant_default && (
                      <Badge className="absolute top-2 left-2 bg-gray-500 text-white text-xs">
                        Default
                      </Badge>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2">
                    <p className="font-medium text-sm truncate">{template.template_name}</p>
                    {!hasAccess && template.locked_reason && (
                      <p className="text-xs text-gray-500">{template.locked_reason}</p>
                    )}
                  </div>
                </div>
              )})}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                variant="outline"
                onClick={handleConfirm} 
                disabled={!selectedTemplate}
                className="border-transparent hover:opacity-90"
                style={{ background: 'var(--qt-gradient)', color: '#ffffff' }}
                data-testid="confirm-template-btn"
              >
                {outputLabels[outputType] || 'Continue'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelector;
